import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Cpu, Radio, Brain, Sparkles, CircuitBoard } from "lucide-react";

/**
 * Hero — Editorial / Brutalist tech with lightweight 3D parallax.
 * - Background: grid skeleton + radial spotlight
 * - Foreground: massive headline, kinetic outline word
 * - Layered "floating" data cards with mouse-parallax
 * - Spinning circular badge
 */
export default function Hero() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const { innerWidth, innerHeight } = window;
      setMouse({
        x: (e.clientX / innerWidth - 0.5) * 2,
        y: (e.clientY / innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const tx = (depth) => `translate3d(${mouse.x * depth}px, ${mouse.y * depth}px, 0)`;

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden grid-bg spotlight noise-overlay border-b border-[var(--ri-ink)]"
    >
      <div className="ri-container pt-12 pb-24 md:pt-20 md:pb-32 relative">
        {/* Background big outline word restored */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-32 md:top-24 pointer-events-none select-none opacity-10"
          style={{ transform: tx(-8) }}
        >
          <div className="kinetic-outline text-[20vw] leading-none uppercase whitespace-nowrap pl-4">
            GAKUEN·TECH
          </div>
        </div>

        <div className="relative grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — Headline */}
          <div className="lg:col-span-7 relative">
            <div className="flex items-center gap-3 mb-6">
              <span className="ri-tag ri-tag--orange">
                <Sparkles size={12} /> RoboInsci × Innovation
              </span>
              <span className="hidden md:inline ri-tag">EST · 2019</span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
              className="font-display text-[clamp(2.6rem,7.5vw,7rem)] leading-[0.92] tracking-[-0.04em]"
            >
              We don't just{" "}
              <span className="font-display-soft italic">teach</span>{" "}
              technology — we deliver{" "}
              <span className="relative inline-block">
                <span className="relative z-10">future-ready</span>
                <span
                  aria-hidden
                  className="absolute -bottom-2 left-0 right-0 h-3 bg-[var(--ri-orange)] -z-0"
                />
              </span>{" "}
              ecosystems.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 max-w-xl text-[var(--ri-muted)] text-lg leading-relaxed"
            >
              <span className="text-[var(--ri-ink)] font-medium">RoboInsci</span> is our flagship innovation & training ecosystem — workshops, mentorship and full institutional labs in robotics, Gen AI, IoT, app-dev and 3D-printing.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/contact" data-testid="hero-cta-book" className="btn-primary">
                Book RoboInsci program <ArrowUpRight size={14} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-lab" className="btn-ghost">
                Setup Lab <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          </div>

          {/* RIGHT — Large Logo + Animations Restored */}
          <div className="lg:col-span-5 relative h-[400px] md:h-[540px] flex items-center justify-center">
            {/* Spinning badge restored */}
            <div
              className="absolute -top-10 -right-4 w-32 h-32 rounded-full border border-[var(--ri-ink)] grid place-items-center bg-[var(--ri-bg)] z-30"
              style={{ transform: tx(10) }}
              data-testid="hero-spin-badge"
            >
              <svg viewBox="0 0 100 100" className="absolute inset-0 spin-slow w-full h-full">
                <defs>
                  <path
                    id="circle-text"
                    d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0"
                  />
                </defs>
                <text className="font-mono uppercase" fontSize="9" fill="#0a0a0a" letterSpacing="3">
                  <textPath href="#circle-text">
                    ROBO · INSCI · INNOVATION · ECOSYSTEM ·
                  </textPath>
                </text>
              </svg>
              <div className="text-center">
                <div className="font-display text-[10px] uppercase tracking-[0.2em] text-[var(--ri-muted)]">
                  Build
                </div>
                <div className="font-display text-2xl">2026</div>
              </div>
            </div>

            {/* Large Logo with floating effect */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              style={{ transform: tx(20) }}
              className="float-slow z-20"
            >
              <img
                src="/assets/images/roboinsci_logo.png"
                alt="RoboInsci"
                className="w-full max-w-[320px] md:max-w-[420px] h-auto object-contain"
              />
            </motion.div>

            {/* Accents restored */}
            <div
              className="absolute -bottom-4 right-0 w-24 h-24 diagonal-stripes-orange opacity-90 z-0"
              style={{ transform: tx(-10) }}
            />
            <CircuitBoard
              className="absolute bottom-10 left-0 text-[var(--ri-ink)] opacity-10"
              size={120}
              style={{ transform: tx(15) }}
              aria-hidden
            />
          </div>
        </div>
      </div>
    </section>
  );
}
